<template>
  <div class="seeker-job-card">
    <section class="relative">
      <a class="block w-full" :href="jobLink" @click.prevent="emitJobClick">
        <SeekerJobCardDescription ref="jobDescription" :job="job" :accountID="account.id" :showBanner="showBanner" />
      </a>
      <Button
        v-if="displayDots"
        id="modal-toggle"
        class="seeker-job-card-modal absolute top-8 right-8"
        :class="popupClasses"
        variant="text"
        :aria-label="modalToggleLabel"
        @click="$emit('manage-job', job)"
      >
        <MoreIcon />
      </Button>
    </section>
    <slot></slot>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Button from '@/components/button/Button.vue';
import MoreIcon from '@/graphics/icons/MoreIcon.vue';
import helperStatus from '@/helpers/status';
import { Account } from '@/models/account';

import SeekerJobCardDescription from './SeekerJobCardDescription.vue';

export default defineComponent({
  name: 'SeekerJobCard',
  components: {
    Button,
    MoreIcon,
    SeekerJobCardDescription,
  },
  props: {
    job: {
      type: Object as () => any,
      required: true,
    },
    account: {
      type: Object as () => Account,
      required: true,
    },
  },
  emits: ['click', 'manage-job'],
  data(): {
    helperStatus: typeof helperStatus;
  } {
    return {
      helperStatus,
    };
  },
  computed: {
    // UI State
    modalToggleLabel(): string {
      return `Manage your job: ${this.job.title}`;
    },
    showBanner(): boolean {
      // always show banner for pending jobs
      if (this.job.status === 'PENDING') return true;

      // if job is not active, don't show banner
      if (!this.job.isActive) return false;

      // finally, show the banner if job has new applicants
      return this.job.applications.edges.some(({ node }) => node.parentInterestStatus === 'RECEIVED');
    },
    popupClasses(): Record<string, boolean> {
      return { '__with-banner': this.showBanner };
    },
    displayDots(): boolean {
      const parentOwnsJob = this.account.id === this.job.parent.id;
      const hasOpenApplicants = this.totalOpenApplicants.length > 0;

      // some jobs are owned by the parent, some are owned by family concierge. If the parent owns the job, we allow them to manage the job.
      // if the job has open applicants, we show the menu because it has a link to the applicants page
      return parentOwnsJob || hasOpenApplicants;
    },
    totalOpenApplicants(): Array<{ node: { status: string } }> {
      return this.job.applications.edges.filter((app) => app.node.status === 'open');
    },
    totalNewApplicants(): number {
      return this.job.applications.edges.filter(({ node }) => node.parentInterestStatus === 'RECEIVED').length;
    },
    jobLink(): string {
      if (this.totalNewApplicants > 0) {
        return `/jobs/${this.job.id}/applications`;
      }
      // Legacy routes use full page navigation (not Vue Router)
      const isPending = this.job.status === 'PENDING';
      if (this.job.isActive || isPending) {
        return '/seeker/my-jobs/opened';
      }

      return '/seeker/my-jobs/closed';
    },
  },
  methods: {
    emitJobClick() {
      this.$emit('click', this, this.job);
    },
  },
});
</script>

<style lang="scss" scoped>
.seeker-job-card {
  flex-flow: column;
  background: white;
  box-shadow: 0 2px 4px 0 rgba(5, 7, 15, 0.2);
  border-radius: 4px;
  display: flex;
  transition: 0.5s box-shadow ease-in;

  &:hover {
    box-shadow: 0 4px 16px 0 rgba(5, 7, 15, 0.2);
  }
}

.seeker-job-card-modal {
  margin-left: auto;
  position: absolute;

  &.__with-banner {
    top: 2.5rem;
  }
}
</style>
